<template>
    <div>
        <div class="modal" tabindex="-1" role="dialog" id="c2b-billing-simple-filter-modal">
            <div class="modal-dialog" role="document">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Filtro de faturamento</h5>
                    </div>

                    <div class="modal-body">
                        <div>
                            <div class="row">
                                <div class="col-12 col-md-6">
                                    <div class="form-group mb-0">
                                        <label>Data inicial</label>
                                        <v-date-picker 
                                            :input-props="{'class': 'form-control'}" 
                                            popover-visibility="focus"
                                            v-model="billingSimpleGraphicFilter.date.start" />
                                    </div>
                                </div>

                                <div class="col-12 col-md-6">
                                    <div class="form-group mb-0">
                                        <label>Data final</label>
                                        <v-date-picker 
                                            :input-props="{'class': 'form-control'}" 
                                            popover-visibility="focus"
                                            v-model="billingSimpleGraphicFilter.date.end" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="modal-footer">
                        <button type="button" 
                            class="btn btn-primary"
                            :disabled="loadingFilter || !billingSimpleGraphicFilter.isFilterFilled()"
                            @click="getInfoByFilter(billingSimpleGraphicFilter)">
                            Filtrar

                            <fa icon="sync-alt" spin v-show="loadingFilter"/>
                        </button>
                        
                        <button type="button" 
                            class="btn btn-outline-secondary" 
                            @click="clearFilter(billingSimpleGraphicFilter)">Restaurar</button>

                        <button type="button" 
                            class="btn btn-outline-secondary" 
                            @click="closeFilterModal()">Fechar</button>
                    </div>
                </div>
            </div>
        </div>

        <div class="c2b-card">
            <div class="c2b-card-title">
                <button type="button" 
                    class="btn btn-outline-secondary btn-sm float-right"
                    @click="showFilterModal()">
                    <fa icon="calendar-alt" />
                </button>

                <p class="float-left">Faturamento</p>
                <br>
                <p class="float-left c2b-card-subtitle">{{billingSimpleGraphicFilterSubmitted.date.start | dateFilter}} até {{billingSimpleGraphicFilterSubmitted.date.end | dateFilter}}</p>
            </div>

            <div>
                <highcharts :options="billingGraphicChartOptions" ref="chart"/>
            </div>
        </div>
    </div>    
</template>

<script>
import {cloneDeep, groupBy, round, sumBy, uniqBy} from 'lodash'
import reportService from '@/report/services/ReportService'
import {createGraphic} from '@/utils/normalizers/GraphicNormalizer'
import { BillingSimpleModel } from '../../dashboard/models/BillingSimpleModel'
import { BillingSimpleGraphicFilterModel } from '../../dashboard/models/BillingSimpleGraphicFilterModel'
import ModalService from '../../utils/services/ModalService'
import { dateFilter } from '@/utils/filters/DateTimeFilter'

const ID_FILTER_MODAL = '#c2b-billing-simple-filter-modal'

function getMonths(billingSimpleList) {
    return uniqBy(billingSimpleList, 'month').map((b) => {
        return b.month
    })
}

function getTotalByMonth(billingSimpleList) {
    const dataGroupedByMonth = groupBy(billingSimpleList, 'month')
    const data = []

    for (const key in dataGroupedByMonth) {
        if (!!key && key.indexOf('/') > -1) {
            for (const inf of dataGroupedByMonth[key]) {
                if (inf.month === key) {                    
                    data.push({
                        y: sumBy(dataGroupedByMonth[key], 'total'),
                        metadata: {
                            type: inf.type,
                            month: inf.month,
                            total: inf.total,
                        },
                    })
                }
            }
        }
    }

    return uniqBy(data, 'metadata.month')
}

function getTotalByTypeByMonth(billingSimpleList, type) {
    const dataGroupedByMonth = groupBy(billingSimpleList, 'month')

    for (const key in dataGroupedByMonth) {
        if (key.indexOf('/') > -1) {
            let hasTheType = false

            for (const d of dataGroupedByMonth[key]) {
                if (d.type === type) {
                    hasTheType = true
                }
            }

            if (!hasTheType) {
                dataGroupedByMonth[key].push(new BillingSimpleModel({
                    type,
                    total: 0,
                    month: key,
                }))
            }
        }
    }

    let data = []
    
    for (const key in dataGroupedByMonth) {
        if (!!key.indexOf('/') > -1) {
            data.push(...dataGroupedByMonth[key])
        }
    }
    
    data = data.map((d) => {
        let total = 0

        if (d.type === type) {
            total = d.total
        }

        return {
            y: round(total, 2),
            metadata: {
                total: round(d.total, 2),
                month: d.month,
                type: d.type,
            },
        }
    })

    return data.filter((d) => d.metadata.type === type)
}

export default {
    name: 'BillingSimpleGraphicCmp',
    filters: {
        dateFilter,
    },
    data() {
        return {
            loadingFilter: false,
            billingSimpleGraphicFilter: new BillingSimpleGraphicFilterModel(),
            billingSimpleGraphicFilterSubmitted: new BillingSimpleGraphicFilterModel(),
            billingSimpleList: [],
            billingGraphicChartOptions: createGraphic({
                chart: {
                    type: 'column',
                },
                xAxis: {
                    categories: []
                },
                tooltip: {
                    shared: true
                },
                series: [
                    {
                        type: 'column',
                        name: 'Total',
                        data: [],
                    },
                    {
                        type: 'line',
                        name: 'Recorrência',
                        data: [],
                    },
                    {
                        type: 'line',
                        name: 'Excedente',
                        data: [],
                    },
                    {
                        type: 'line',
                        name: 'Específico',
                        data: [],
                    },
                    {
                        type: 'line',
                        name: 'Adesão',
                        data: [],
                    },
                ]
            }),  
        }
    },
    methods: {
        async drawGraphic(filter) {
            this.billingSimpleGraphicFilterSubmitted = cloneDeep(filter)

            try {
                this.billingSimpleList = await reportService.getBillingSimpleList(filter)

                this.$refs.chart.chart.xAxis[0].setCategories(getMonths(this.billingSimpleList))
                this.$refs.chart.chart.series[0].setData(getTotalByMonth(this.billingSimpleList)) 
                this.$refs.chart.chart.series[1].setData(getTotalByTypeByMonth(this.billingSimpleList, 'Recorrência'))
                this.$refs.chart.chart.series[2].setData(getTotalByTypeByMonth(this.billingSimpleList, 'Excedente'))
                this.$refs.chart.chart.series[3].setData(getTotalByTypeByMonth(this.billingSimpleList, 'Específico'))
                this.$refs.chart.chart.series[4].setData(getTotalByTypeByMonth(this.billingSimpleList, 'Adesão'))
            } catch(err) {
                this.$floatingAlert.error({
                    message: err.message,
                })
            }
        },
        async getInfoByFilter(filter) {
            this.loadingFilter = true

            try {
                await this.drawGraphic(filter)
                this.closeFilterModal()
            } finally {
                this.loadingFilter = false
            }
        },
        showFilterModal() {
            ModalService.show(ID_FILTER_MODAL)
        },
        closeFilterModal() {
            ModalService.hide(ID_FILTER_MODAL)
        },
        clearFilter() {
            this.billingSimpleGraphicFilter = new BillingSimpleGraphicFilterModel()
        },
    },
    async mounted() {
        await this.drawGraphic(this.billingSimpleGraphicFilter)
    },
}
</script>
